import { Button, ConfirmModal, notify, RowInput } from '@affine/component';
import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
import { ResearchAreaPicker } from '@affine/core/components/researcher-onboarding/research-area-picker';
import {
  AuthService,
  DefaultServerService,
  type Server,
  ServersService,
} from '@affine/core/modules/cloud';
import {
  type DialogComponentProps,
  type GLOBAL_DIALOG_SCHEMA,
  GlobalDialogService,
} from '@affine/core/modules/dialogs';
import { WorkspacesService } from '@affine/core/modules/workspace';
import { buildEmptyResearchWorkspace } from '@affine/core/utils/first-app-data';
import { updateWorkspaceContextMutation } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import track from '@affine/track';
import { FrameworkScope, useLiveData, useService } from '@toeverything/infra';
import { useCallback, useState } from 'react';

import * as styles from './index.css';

const FormSection = ({
  label,
  input,
}: {
  label: string;
  input: React.ReactNode;
}) => {
  return (
    <section className={styles.section}>
      <label className={styles.label}>{label}</label>
      {input}
    </section>
  );
};

export const CreateWorkspaceDialog = ({
  serverId,
  close,
  ...props
}: DialogComponentProps<GLOBAL_DIALOG_SCHEMA['create-workspace']>) => {
  const t = useI18n();

  const [workspaceName, setWorkspaceName] = useState('');
  const [researchGoal, setResearchGoal] = useState('');
  const [researchAreas, setResearchAreas] = useState<string[]>([]);
  const inputServerId = serverId ?? 'affine-cloud';

  const serversService = useService(ServersService);
  const server = useLiveData(
    inputServerId ? serversService.server$(inputServerId) : null
  );

  const onOpenChange = useCallback(
    (open: boolean) => {
      if (!open) close();
    },
    [close]
  );

  return (
    <ConfirmModal
      open
      onOpenChange={onOpenChange}
      title={t['com.affine.nameWorkspace.title']()}
      description={t['com.affine.nameWorkspace.description']()}
      cancelText={t['com.affine.nameWorkspace.button.cancel']()}
      closeButtonOptions={{
        ['data-testid' as string]: 'create-workspace-close-button',
      }}
      contentOptions={{}}
      childrenContentClassName={styles.content}
      customConfirmButton={() => {
        return (
          <FrameworkScope scope={server?.scope}>
            <CustomConfirmButton
              workspaceName={workspaceName}
              researchGoal={researchGoal}
              researchAreas={researchAreas}
              server={server}
              onCreated={res =>
                close({ metadata: res.meta, defaultDocId: undefined })
              }
            />
          </FrameworkScope>
        );
      }}
      {...props}
    >
      <FormSection
        label={t['com.affine.nameWorkspace.subtitle.workspace-name']()}
        input={
          <RowInput
            autoFocus
            className={styles.input}
            data-testid="create-workspace-input"
            placeholder={t['com.affine.nameWorkspace.placeholder']()}
            maxLength={64}
            minLength={0}
            onChange={setWorkspaceName}
          />
        }
      />

      <FormSection
        label="Research focus (optional)"
        input={
          <textarea
            className={styles.input}
            style={{
              minHeight: 64,
              fontFamily: 'inherit',
              resize: 'vertical',
              padding: '8px 12px',
            }}
            placeholder="e.g. Investigating tau aggregation in iPSC-derived neurons"
            maxLength={500}
            value={researchGoal}
            onChange={e => setResearchGoal(e.target.value)}
          />
        }
      />

      <FormSection
        label="Research areas (optional)"
        input={
          <ResearchAreaPicker
            value={researchAreas}
            onChange={setResearchAreas}
          />
        }
      />
    </ConfirmModal>
  );
};

const CustomConfirmButton = ({
  workspaceName,
  researchGoal,
  researchAreas,
  server,
  onCreated,
}: {
  workspaceName: string;
  researchGoal: string;
  researchAreas: string[];
  server?: Server | null;
  onCreated: (res: Awaited<ReturnType<typeof buildEmptyResearchWorkspace>>) => void;
}) => {
  const t = useI18n();
  const [loading, setLoading] = useState(false);

  const session = useService(AuthService).session;
  const loginStatus = useLiveData(session.status$);
  const globalDialogService = useService(GlobalDialogService);
  const workspacesService = useService(WorkspacesService);
  const defaultServerService = useService(DefaultServerService);

  const openSignInModal = useCallback(() => {
    globalDialogService.open('sign-in', { server: server?.baseUrl });
  }, [globalDialogService, server?.baseUrl]);

  const handleConfirm = useAsyncCallback(async () => {
    if (loading) return;
    setLoading(true);
    track.$.$.$.createWorkspace({
      flavour: 'affine-cloud',
    });

    try {
      const res = await buildEmptyResearchWorkspace(
        workspacesService,
        'affine-cloud',
        workspaceName
      );

      const trimmedGoal = researchGoal.trim();
      if (trimmedGoal || researchAreas.length > 0) {
        try {
          await defaultServerService.server.gql({
            query: updateWorkspaceContextMutation as any,
            variables: {
              input: {
                workspaceId: res.meta.id,
                researchGoal: trimmedGoal || undefined,
                keyTerms: researchAreas.length > 0 ? researchAreas : undefined,
              },
            },
          } as any);
        } catch (err) {
          console.error('Failed to seed workspace research context', err);
        }
      }

      onCreated(res);
    } catch (e) {
      console.error(e);
      notify.error({
        title: 'Failed to create workspace',
        message: 'please try again later.',
      });
    } finally {
      setLoading(false);
    }
  }, [
    defaultServerService,
    loading,
    onCreated,
    researchAreas,
    researchGoal,
    server,
    workspaceName,
    workspacesService,
  ]);

  const handleCheckSessionAndConfirm = useCallback(() => {
    if (server && loginStatus !== 'authenticated') {
      return openSignInModal();
    }
    handleConfirm();
  }, [handleConfirm, loginStatus, openSignInModal, server]);

  return (
    <Button
      disabled={!workspaceName}
      data-testid="create-workspace-create-button"
      variant="primary"
      onClick={handleCheckSessionAndConfirm}
      loading={loading}
    >
      {t['com.affine.nameWorkspace.button.create']()}
    </Button>
  );
};
